import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchQueuedAnswers, addToQueue } from "../redux/actions";
import { formAnswerSelectors } from "../redux/selectors";

const FETCH_TIMEOUT = 300;

// Fetches remote variable values from form answers and stores them to redux state
// Provides loading flag to indicate if loading is in progress.
const useRemoteVariableValues = (
  shouldFetchVariableValues,
  projectId,
  variables = []
) => {
  if (!projectId || !shouldFetchVariableValues) return [{}];

  // Selector instance should always be the same for the same component instance
  const variableValueSelector = useMemo(
    formAnswerSelectors.makeVariableValuesSelector,
    []
  );

  const variablesLoadingSelector = useMemo(
    formAnswerSelectors.makeVariablesLoadingSelector,
    []
  );

  // values from redux state
  const values = useSelector((state) =>
    variableValueSelector(state, { projectId, variables })
  );

  // loading from redux state
  const loading = useSelector((state) =>
    variablesLoadingSelector(state, { projectId, variables })
  );

  const dispatch = useDispatch();

  const fetchValue = (v) => {
    dispatch(addToQueue({ projectId, ...v }));

    // After timeout, all queued variable values are fetched at once to avoid making dozens of requests.
    window.setTimeout(() => {
      dispatch(fetchQueuedAnswers(projectId));
    }, FETCH_TIMEOUT);
  };

  useEffect(() => {
    variables.forEach((v) => fetchValue(v));
  }, [shouldFetchVariableValues, projectId, variables]);

  return [values, loading];
};

export default useRemoteVariableValues;
